import { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";

import { Store } from "../../../../types";
import { useHasScope } from "../../../../utils/Permission";
import { enableStore, takeStoreOffline } from "../../../../lib/admin";
import { deleteSearchIndex, deleteStore, deleteStoreMirror } from "../../../../lib/storeComponents";
import { runLoggedAction } from "../../../logs/logUtils";
import ConfirmModal from "../../../global/ConfirmModal";
import DeleteConfirmModal from "../../../global/DeleteConfirmModal";
import Notification from "../../../global/Notification";
import SyncStatusModal from "../SyncStatusModal";
import DecommissionProgress from "./DecommissionProgress";
import DecommissionStep from "./DecommissionStep";
import { StepKey, buildDecommissionSteps } from "./decommissionSteps";
import { useComponentStatuses } from "./useComponentStatuses";
import deleteStoreIcon from "../../../../assets/management/trash.svg";

const COMPONENT_DELETIONS = [
    { key: "searchIndex", name: "product search data", section: "searchIndex", remove: deleteSearchIndex },
    { key: "mirror", name: "synced store data", section: "mirrorDatabase", remove: deleteStoreMirror },
] as const;

type Props = {
    store: Store;
    onStoreChange: () => void;
};

export default function DecommissionTab({ store, onStoreChange }: Props) {
    const [refreshKey, setRefreshKey] = useState(0);
    const [dialog, setDialog] = useState<StepKey | null>(null);
    const [isProcessing, setIsProcessing] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const navigate = useNavigate();

    const access = {
        syncSettings: useHasScope("update:storesyncsettings"),
        retailSystem: useHasScope("update:rsinformation"),
    };

    const statuses = useComponentStatuses(store, refreshKey);
    const steps = buildDecommissionSteps(store, statuses, access);
    const completed = steps.filter((step) => step.state === "done").length;
    const currentKey = steps.find((step) => step.state !== "done")?.key;

    const closeDialog = () => setDialog(null);
    const closeError = useCallback(() => setErrorMessage(null), []);
    const recheckComponents = () => setRefreshKey((key) => key + 1);

    const recheckAll = () => {
        recheckComponents();
        onStoreChange();
    };

    const updateStore = async (update: () => Promise<unknown>, failureMessage: string) => {
        if (isProcessing) return;
        setIsProcessing(true);

        try {
            await update();
            onStoreChange();
        } catch {
            setErrorMessage(failureMessage);
        } finally {
            setIsProcessing(false);
            closeDialog();
        }
    };

    const turnOffSync = (reason: string) =>
        updateStore(() => enableStore(store.id, false, reason), "Could not turn off syncing. Please try again.");

    const takeOffline = () =>
        updateStore(() => takeStoreOffline(store), "Could not take the store offline. Please try again.");

    const deleteComponent = (section: string, remove: (storeId: number) => Promise<string>) => () =>
        runLoggedAction(
            {
                storeId: store.id,
                entityType: 'components',
                section,
                action: 'delete',
                newValue: { storeName: store.friendlyName },
            },
            () => remove(store.id),
        );

    const cancelStore = () =>
        runLoggedAction(
            {
                storeId: store.id,
                entityType: 'store',
                action: 'cancel',
                newValue: { storeName: store.friendlyName },
            },
            () => deleteStore(store.id),
        );

    const closeDeletion = (wasDeleted: boolean) => {
        closeDialog();
        if (wasDeleted) recheckComponents();
    };

    const closeCancellation = (wasCancelled: boolean) => {
        if (wasCancelled) navigate("/", { replace: true });
        else closeDialog();
    };

    return (
        <div className="decommission">
            <div className="decommission__header">
                <p>Shopify Stores/{store.friendlyName}</p>
                <h1><img src={deleteStoreIcon} alt="" />Delete Store</h1>
            </div>
            <hr className="decommission__divider" />

            <DecommissionProgress completed={completed} total={steps.length} onRecheck={recheckAll} />

            <ol className="decommission__steps">
                {steps.map((step, index) => (
                    <DecommissionStep
                        key={step.key}
                        number={index + 1}
                        step={step}
                        isCurrent={step.key === currentKey}
                        onAction={() => setDialog(step.key)}
                    />
                ))}
            </ol>

            <SyncStatusModal
                open={dialog === "syncOff"}
                isEnabling={false}
                isProcessing={isProcessing}
                onClose={closeDialog}
                onConfirm={turnOffSync}
            />

            <ConfirmModal
                open={dialog === "offline"}
                title="Take the store offline"
                message={`${store.friendlyName} will stop getting data from RetailSystem. You can set it back to Live from the RetailSystem tab.`}
                confirmLabel="Take offline"
                onCancel={closeDialog}
                onConfirm={takeOffline}
            />

            {COMPONENT_DELETIONS.map(({ key, name, section, remove }) => (
                <DeleteConfirmModal
                    key={key}
                    open={dialog === key}
                    title={`Delete ${name}`}
                    message={
                        <>
                            This will permanently delete the {name} of <strong>{store.friendlyName}</strong>.
                            This action cannot be undone.
                        </>
                    }
                    onConfirm={deleteComponent(section, remove)}
                    onClose={closeDeletion}
                />
            ))}

            <DeleteConfirmModal
                open={dialog === "cancel"}
                title="Cancel the store"
                message={
                    <>
                        This will permanently remove <strong>{store.friendlyName}</strong> from the RS Admin
                        Portal. This action cannot be undone.
                    </>
                }
                onConfirm={cancelStore}
                onClose={closeCancellation}
            />

            <Notification message={errorMessage} type="error" onClose={closeError} />
        </div>
    );
}
